// Prompt Rule Management Service
// API calls for managing prompt extraction rules

import { acquireTokenSilent } from '../config/msalConfig';

export interface PromptRule {
  id: string;
  section: string;
  field_name: string;
  rule_text: string;
  priority: number;
  description: string;
  status: 'active' | 'inactive' | 'testing';
  prompt_type?: 'referral' | 'triage' | 'location' | 'clinic_selection' | 'referrer_type';
  created_at: string;
  updated_at: string;
}

export interface GeneratedPrompt {
  sections: {
    [key: string]: string;
  };
  complete_prompt: string;
  generated_at: string;
}

export class PromptRuleService {
  private static baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  /**
   * Get all prompt rules
   * Requires authentication and admin role
   * @param promptType - Optional filter by 'referral' or 'triage'
   */
  static async getRules(promptType?: 'referral' | 'triage' | 'location' | 'clinic_selection' | 'referrer_type'): Promise<PromptRule[]> {
    try {
      const url = new URL(`${this.baseUrl}/prompt-rules`);
      if (promptType) {
        url.searchParams.append('prompt_type', promptType);
      }

      const token = await this.getToken();
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to fetch prompt rules`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Error fetching prompt rules:', error);
      throw error;
    }
  }

  /**
   * Update a prompt rule status
   */
  static async updateStatus(ruleId: string, status: string): Promise<PromptRule> {
    return this.updateRule(ruleId, { status: status as 'active' | 'inactive' | 'testing' });
  }

  /**
   * Update a prompt rule (any fields)
   */
  static async updateRule(ruleId: string, updates: Partial<PromptRule>): Promise<PromptRule> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/prompt-rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to update rule`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating prompt rule:', error);
      throw error;
    }
  }

  /**
   * Generate the final extraction prompt from all active rules
   * @param promptType - 'referral' for referral extraction prompts, 'triage' for triage extraction prompts
   */
  static async generatePrompt(promptType: 'referral' | 'triage' | 'location' | 'clinic_selection' = 'referral'): Promise<GeneratedPrompt> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/prompt-rules/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt_type: promptType }),
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to generate prompt`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating prompt:', error);
      throw error;
    }
  }

  /**
   * Create a new prompt rule
   * @param rule - Rule data including prompt_type (optional, will be inferred from section if not provided)
   */
  static async createRule(rule: Omit<PromptRule, 'id' | 'created_at' | 'updated_at'>): Promise<PromptRule> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/prompt-rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(rule),
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to create rule`;
        let errorData: any;
        try {
          errorData = await response.json();
          console.error('Server error response:', errorData);
          message = errorData.message || errorData.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      const data = await response.json();
      console.log('Created rule response:', data);
      
      // Validate that we have an id
      if (!data.id) {
        console.error('Response missing id field:', data);
        throw new Error('Server returned invalid rule data: missing id field');
      }
      
      return data;
    } catch (error) {
      console.error('Error creating prompt rule:', error);
      throw error;
    }
  }

  /**
   * Get Entra ID token from MSAL
   */
  private static async getToken(): Promise<string> {
    try {
      const tokenResult = await acquireTokenSilent();
      return tokenResult.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return '';
    }
  }
}
